import React from 'react';
import styled from 'styled-components/macro';
import { themeColors } from '../../theme/theme';
import { FlexRow } from '../basic/Flex';

const HiddenRadio = styled.input.attrs({ type: 'radio' })`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const StyledRadioContainer = styled.label<{ checked: boolean }>`
  position: relative; // important for ::before positioning
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid ${themeColors.m2Black};
  background-color: ${themeColors.m2White};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  z-index: 0;

  &:hover,
  &:focus-within {
    background: ${themeColors.darkSnow};
    box-shadow: 0 0 0 8px ${themeColors.darkSnow};
  }
`;

const StyledDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${themeColors.m2Black};
`;

const OptionLabel = styled.span`
  cursor: pointer;
  line-height: 24px;
`;

type RadioOption<T> = {
  label: string;
  value: T;
};

type RadioGroupProps<T> = {
  id: string;
  name: string;
  options: RadioOption<T>[];
  selectedValue: T;
  onChange: (value: T) => void;
};

export function RadioGroup<T extends string | number | boolean>({
  id,
  name,
  options,
  selectedValue,
  onChange,
}: RadioGroupProps<T>) {
  return (
    <FlexRow gap="24px" id={id}>
      {options.map((option, index) => {
        const buttonId = `${name}-${index}`;
        const checked = option.value === selectedValue;

        return (
          <FlexRow key={buttonId} gap="12px" justifyContent="center">
            <StyledRadioContainer htmlFor={buttonId} checked={checked}>
              <HiddenRadio
                id={buttonId}
                name={name}
                value={String(option.value)}
                checked={checked}
                onChange={() => onChange(option.value)}
              />
              {checked && <StyledDot />}
            </StyledRadioContainer>
            <OptionLabel onClick={() => onChange(option.value)}>{option.label}</OptionLabel>
          </FlexRow>
        );
      })}
    </FlexRow>
  );
}
